import { differenceInDays, differenceInMonths } from 'date-fns';
import cloudIcon from 'img/icons/rebranding-cloud.svg?url';
import { ascend, descend, path, sortWith, values } from 'ramda';
import { BillingDataPlatform } from 'reactApp/api/billing/data/BillingDataAPICall.types';
import { abHeavyFileBuy, abHeavyFileBuyTariffsList, abHeavyFileBuyV3 } from 'reactApp/appHelpers/featuresHelpers/features/heavyFileBuy';
import type { SpaceListValues } from 'reactApp/constants/spaceList';
import { getBaseUserQuota } from 'reactApp/modules/family/family.selectors';
import type { RootState } from 'reactApp/store';
import type { LoadingState } from 'reactApp/types/commonStates';
import { PERIODS } from 'reactApp/utils/Period';
import { createSelector } from 'reselect';

import {
    type BaseSubscriptionType,
    type BillingDataState,
    type BillingDataSubscriptionInfo,
    BillingDataSubscriptionInfoStatus,
} from './billingData.types';

const getBasicSubscription = createSelector(
    (state: RootState) => getBaseUserQuota(state),
    (baseQuota): BaseSubscriptionType => ({
        id: 'base',
        space: baseQuota,
        icon: cloudIcon,
        base: true,
        type: BillingDataPlatform.base,
    })
);

export const getBillingDataRoot = (state: RootState) => state.billingData;
const getBilligDataSubscriptions = createSelector(getBillingDataRoot, ({ subscription }) => subscription);

const getBillindDataRaw = createSelector(getBillingDataRoot, ({ rawBillingData }) => rawBillingData);

const getServicesData = createSelector(getBillindDataRaw, ({ services }) => services);
const getCloudServicesData = createSelector(getServicesData, ({ cloud }) => cloud);
const getCommonServicesData = createSelector(getServicesData, ({ common }) => common);

/**
 * @description Информация о доступности создания семьи, только на основании данных билинга.
 *
 * Информация о доступности создания семейной подписки на основании данных о пользователе
 * и ответа ручек family/* доступна в userCanCreateFamily в reactApp/modules/family/family.selectors.ts
 */
const isUserCanCreateFamilyByBilling = createSelector(getCommonServicesData, (common) => !!common?.family);

const isUserWithFamilyFolders = createSelector(getCloudServicesData, (cloud) => !!cloud?.family?.folders);
const isUserWithFamilyAlbums = createSelector(getCloudServicesData, (cloud) => !!cloud?.family?.albums);

const getSubscriptionsList = createSelector(getBilligDataSubscriptions, (list) => values(list));

const getMonthSubscriptionsBySignInProductId = createSelector(getSubscriptionsList, (subscriptions) => {
    return subscriptions.filter((sub) => {
        const splits = sub.productId.split('_');
        return splits[1] === PERIODS.month || splits[0].includes(PERIODS.month);
    });
});

const getPaidInfo = createSelector(getBillingDataRoot, (data) => data.paidInfo);

const getSubscriptionsById = createSelector(
    getBilligDataSubscriptions,
    (_: RootState, id: string | number) => id,
    (subscriptions, id) => path<BillingDataSubscriptionInfo>([id], subscriptions)
);

const getTrialSubscription = createSelector(
    getBilligDataSubscriptions,
    (subscriptions) => subscriptions && Object.values(subscriptions).find((item) => item.isTrial)
);

const getAutorenewalTrialSubscription = createSelector(getBilligDataSubscriptions, (subscriptions) => {
    const trials = subscriptions && Object.values(subscriptions).filter((subs) => subs.isTrial && subs.autorenewal);
    return trials.length && trials[0];
});

const sortSubscriptionsBySpace = createSelector(getBilligDataSubscriptions, (subscriptions) => {
    /* tempexp_18869-start tempexp_18955-start */
    let productsToExclude: string[] = [];
    if (abHeavyFileBuy || abHeavyFileBuyV3) {
        productsToExclude = abHeavyFileBuyTariffsList;
    }
    const list = Object.values(subscriptions).filter((item) => !productsToExclude.includes(item.productId));
    /* tempexp_18869-end tempexp_18955-end */
    return sortWith([ascend(path(['space', 'original'])), ascend(path(['id']))], list);
});

const getActiveSubscriptionId = createSelector(getBillingDataRoot, ({ activeSubscriptionId }) => activeSubscriptionId);

const hasMailSubscription = createSelector(getSubscriptionsList, (list) => list.some((subscription) => subscription.isMail));
const hasActiveSubscription = createSelector(getSubscriptionsList, (list) =>
    list.find((subscription) => subscription.status === BillingDataSubscriptionInfoStatus.ACTIVE)
);

export const getBillingDataLoadingState = createSelector(
    getBillingDataRoot,
    (state: BillingDataState): LoadingState => ({
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
    })
);

const isUserWithoutPayment = createSelector(
    getPaidInfo,
    getBillingDataLoadingState,
    (paidInfo, loading) => loading.isLoaded && paidInfo.paidExpires === 0 && paidInfo.trialExpires === 0
);

const lastPaymentWasLessThan = createSelector(
    (_, periodData: { days?: number; months?: number }) => periodData,
    getPaidInfo,
    isUserWithoutPayment,
    (periodData, paidInfo, isWithoutPayment) => {
        if (isWithoutPayment) {
            return false;
        }

        const { trialExpires, paidExpires } = paidInfo;

        const lastPayment = Math.max(trialExpires, paidExpires);

        const { days, months } = periodData;

        if (months && lastPayment) {
            const diff = differenceInMonths(Date.now(), lastPayment);

            return diff < months;
        }

        if (days && lastPayment) {
            const diff = differenceInDays(Date.now(), lastPayment);

            return diff < days;
        }

        return false;
    }
);

const getSubscriptionsCount = createSelector(getBilligDataSubscriptions, (subscriptions) => {
    return Object.values(subscriptions).length;
});

const getSortedMonthSubscriptionsBySpaces = createSelector(
    getMonthSubscriptionsBySignInProductId,
    (state: RootState) => state,
    (_: RootState, spaces: SpaceListValues[]) => spaces,
    (subscriptions, state, spaces) => {
        const list = subscriptions.filter((sub) => spaces.includes(sub.space.original as SpaceListValues));
        return sortWith([descend(path(['space', 'original'])), descend(path(['renewalCost']))], list);
    }
);

const getSubscriptionsByProductIds = createSelector(
    getSubscriptionsList,
    (_: RootState, productIds: string[]) => productIds,
    (subscriptions, productIds) => {
        return subscriptions.filter((sub) => productIds.includes(sub.productId));
    }
);

export const BillingDataSelectors = {
    getBillindDataRaw,
    getSubscriptionsById,
    getTrialSubscription,
    getAutorenewalTrialSubscription,
    sortSubscriptionsBySpace,
    getActiveSubscriptionId,
    hasMailSubscription,
    lastPaymentWasLessThan,
    isUserWithoutPayment,
    getBillingDataLoadingState,
    getSubscriptionsCount,
    getSubscriptionsList,
    getSortedMonthSubscriptionsBySpaces,
    isUserCanCreateFamilyByBilling,
    isUserWithFamilyFolders,
    isUserWithFamilyAlbums,
    hasActiveSubscription,
    getSubscriptionsByProductIds,
    getBasicSubscription,
};
